import { CreateControllerFn, ControllerParams } from 'yoshi-flow-editor-runtime';
import { createEventHandler } from 'yoshi-flow-editor-runtime/tpa-settings';

import { App } from './AppController';
import { AppProps, IEvents, TabState } from '../../../types/common';
import { getUserData } from '../../../utils/user';
import {
  createOrdersApi,
  createPlansApi,
  createPremiumApi,
  createBenefitsApi,
  createGroupsApi,
} from '../../../services';
import { Router } from './Router';
import { Navigation } from './Navigation';
import { ListController } from './ListController';
import { CheckoutController } from './CheckoutController';
import { StatusController } from './StatusController';
import { RestrictedController } from './RestrictedController';
import { apiHeaders } from '../../../services/headers';
import { PricingPlansBi } from '../../../services/bi';
import { Analytics } from '../../../services/analytics';
import { CommonBIDataFromFlowAPI } from '../../../services/CommonBIDataFromFlowAPI';

const createController: CreateControllerFn = async (params: ControllerParams) => {
  const {
    flowAPI,
    controllerConfig: {
      setProps: _setProps,
      wixCodeApi,
      appParams: { instance, appDefinitionId, instanceId },
      config,
    },
  } = params;

  // const baseUrl = wixCodeApi.location.baseUrl.split('/').slice(0, 3).join('/');
  const isSSR = wixCodeApi.window.rendering.env === 'backend';
  const baseUrl = isSSR ? 'https://www.wix.com' : '';

  const headers = apiHeaders({ Authorization: instance });
  const plansApi = createPlansApi(headers, baseUrl);
  const premiumApi = createPremiumApi(headers, baseUrl);
  const ordersApi = createOrdersApi(headers, baseUrl);
  const benefitsApi = createBenefitsApi(headers, baseUrl);
  const groupsApi = createGroupsApi(headers, baseUrl);

  const componentEventHandler = createEventHandler<IEvents>(config.publicData.COMPONENT || {});

  const user = getUserData(wixCodeApi.user.currentUser);
  const noop = () => {};
  const { metaSiteId, ownerId, visitorId } = flowAPI.controllerConfig.platformAPIs.bi ?? {};
  const initialProps: AppProps = {
    appInstanceId: instanceId,
    metaSiteId,
    siteOwnerId: ownerId,
    visitorId,
    hidePopup: noop,
    hideToast: noop,
    showToast: noop,
    closeUpgradeModal: noop,
    continueToDemoCheckout: noop,
    instance,
    loginOnCheckout: noop,
    logout: noop,
    navigateToStatus: noop,
    navigateBackToTPA: noop,
    plans: [],
    popup: null,
    selectPlan: noop,
    selectedPlan: {},
    signupOnCheckout: noop,
    subPage: { name: 'list', integrationData: {} },
    tabState: TabState.REGULAR,
    user,
    missingPlan: false,
    navigateToHomePage: noop,
    navigateBackToPlanList: noop,
    switchAccounts: noop,
    benefits: [],
    trackInitiateCheckout: noop,
    trackSelectPayment: noop,
    demoBuyNowClicked: noop,
    detailedPrice: {},
    navigateBackToCheckout: noop,
    biCheckoutStage: noop,
    biPlanPurchased: noop,
    biThankYouPageCtaButtonClick: noop,
    biUpgradeReferralClick: noop,
  };

  // For some reason when running integration tests it is not possible to set part of props as missing properties gets
  // reverted to initial set values.
  function setProps<T>(x: T) {
    return _setProps(Object.assign(initialProps, x));
  }

  const nav = new Navigation(wixCodeApi, appDefinitionId);
  const bi = new PricingPlansBi(flowAPI.biLogger!, new CommonBIDataFromFlowAPI(flowAPI));
  const analytics = new Analytics(wixCodeApi.window);
  const router = new Router(setProps, nav, analytics, wixCodeApi);
  const list = new ListController(
    setProps,
    wixCodeApi,
    flowAPI,
    router,
    plansApi,
    ordersApi,
    premiumApi,
    bi,
    analytics,
  );
  const checkout = new CheckoutController(
    setProps,
    wixCodeApi,
    router,
    flowAPI,
    plansApi,
    ordersApi,
    benefitsApi,
    premiumApi,
    analytics,
    bi,
  );
  const status = new StatusController(setProps, wixCodeApi, ordersApi, flowAPI, bi, router, plansApi);
  const restricted = new RestrictedController(setProps, wixCodeApi, router, groupsApi, flowAPI);
  const app = new App(setProps, list, checkout, status, restricted, router, flowAPI, bi);

  router.whenInit({ list: async ({ biOptions }) => analytics.setReferralInfo(biOptions?.referralInfo) });
  router.whenNavigate({
    list: async ({ integrationData: { biOptions } }) => analytics.setReferralInfo(biOptions?.referralInfo),
  });

  return {
    async pageReady() {
      // XXX: call to setProps crashes in Editor if done before pageReady().
      setProps(initialProps);

      componentEventHandler.on('tabState', (tabState: TabState) => app.setProps({ tabState }));
      componentEventHandler.onReset(() => app.setProps({ tabState: TabState.REGULAR }));

      wixCodeApi.location.onChange(async () => router.initialize());
      wixCodeApi.site.onInstanceChanged(({ instance: newInstance }) => {
        headers.Authorization = newInstance;
        setProps({ instance: newInstance });
      }, appDefinitionId);

      await app.initialize();
    },
    updateConfig(_$w, newConfig) {
      componentEventHandler.notify(newConfig.publicData.COMPONENT || {});
    },
  };
};

export default createController;
